import { useEffect, useMemo, useRef, useState } from "react";
import QRCode from "qrcode";
import rawCatalog from "./data/tool-catalog.json";
import type { Tool } from "./types";
import { AdminPage } from "./components/AdminPage";
import { AccountPage } from "./components/AccountPage";
import { BusinessTool } from "./components/BusinessTool";
import { FeatureAccessBoundary } from "./components/FeatureAccessBoundary";
import { MonetizationPlacement } from "./components/MonetizationPlacement";
import { I18nProvider, LanguageSwitcher, LocalizedContent, useI18n } from "./i18n";
import { navigate, usePathname } from "./utils/navigation";

const catalog = rawCatalog as Tool[];
const OMITTED_IDS = new Set(["membership"]);
const OMITTED_ROUTES = new Set(["pages/search-entry/index", "pages/example/index", "pages/membership-orders/index"]);
const visibleCatalog = catalog.filter((tool) => !OMITTED_IDS.has(tool.id) && !OMITTED_ROUTES.has(tool.originalRoute));
const FAVORITES_KEY = "xm-web-favorites-v1";

function readFavorites(): string[] {
  try { return JSON.parse(localStorage.getItem(FAVORITES_KEY) || "[]") as string[]; } catch { return []; }
}

function toolUrl(tool: Tool): string {
  return `/tool/${encodeURIComponent(tool.id)}`;
}

function getBrowserVisitorId(): string {
  try {
    const key = "xm-browser-visitor-id-v1";
    const existing = localStorage.getItem(key);
    if (existing && /^[0-9a-f-]{36}$/i.test(existing)) return existing;
    const created = crypto.randomUUID();
    localStorage.setItem(key, created);
    return created;
  } catch {
    return "不可用";
  }
}

type UserAgentHints = {
  architecture?: string;
  bitness?: string;
  model?: string;
  platform?: string;
  platformVersion?: string;
  fullVersionList?: Array<{ brand: string; version: string }>;
};

async function getDeviceDetails(): Promise<Record<string, string>> {
  const ua = navigator.userAgent || "";
  const navigatorWithHints = navigator as Navigator & {
    userAgentData?: {
      brands?: Array<{ brand: string; version: string }>;
      platform?: string;
      getHighEntropyValues?: (hints: string[]) => Promise<UserAgentHints>;
    };
    deviceMemory?: number;
  };
  const uaData = navigatorWithHints.userAgentData;
  let hints: UserAgentHints = {};
  try {
    hints = await uaData?.getHighEntropyValues?.(["architecture", "bitness", "model", "platformVersion", "fullVersionList"]) || {};
  } catch { /* use the reduced user-agent data available in the browser */ }
  const model = hints.model || ua.match(/Android[^;)]*;\s*([^;)]+?)\s+Build\//i)?.[1] || (/iPhone|iPad|iPod/i.test(ua) ? ua.match(/iPad|iPhone|iPod/)?.[0] : "") || "";
  const platform = hints.platform || uaData?.platform || (/Android/i.test(ua) ? "Android" : /iPhone|iPad|iPod/i.test(ua) ? "iOS" : /Windows/i.test(ua) ? "Windows" : /Macintosh|Mac OS X/i.test(ua) ? "macOS" : /Linux/i.test(ua) ? "Linux" : "");
  const manufacturer = /^(iPhone|iPad|iPod|Mac)/i.test(model) || /^(iOS|macOS)$/i.test(platform) ? "Apple" : /Samsung|^SM-|Galaxy/i.test(model) ? "Samsung" : /Pixel/i.test(model) ? "Google" : /HUAWEI|Huawei/i.test(model) ? "Huawei" : /HONOR/i.test(model) ? "Honor" : /Xiaomi|Redmi|POCO/i.test(model) ? "Xiaomi" : /OPPO/i.test(model) ? "OPPO" : /vivo/i.test(model) ? "vivo" : /OnePlus/i.test(model) ? "OnePlus" : "浏览器未提供";
  const versionList = hints.fullVersionList || uaData?.brands || [];
  const browserHint = versionList.find((item) => !/not.?a.?brand/i.test(item.brand));
  const browserVersion = browserHint?.version || ua.match(/(?:Edg|OPR|Chrome|Firefox|Version)\/([\d.]+)/i)?.[1] || "";
  const cores = Number.isFinite(navigator.hardwareConcurrency) ? String(navigator.hardwareConcurrency) : "";
  const memory = Number.isFinite(navigatorWithHints.deviceMemory) ? `约 ${navigatorWithHints.deviceMemory} GB` : "";
  const resolution = typeof screen !== "undefined" ? `${screen.width} × ${screen.height} @ ${window.devicePixelRatio || 1}x` : "";
  return {
    manufacturer,
    model,
    platformVersion: hints.platformVersion || "",
    browserVersion,
    architecture: hints.architecture || "",
    bitness: hints.bitness || "",
    cpuCores: cores,
    deviceMemoryGb: memory,
    screenResolution: resolution,
  };
}

function Header() {
  const { t } = useI18n();
  const [username, setUsername] = useState<string | null>(null);
  useEffect(() => {
    const loadSession = () => { void fetch("/api/auth/session").then((response) => response.json()).then((session: { authenticated?: boolean; username?: string | null }) => setUsername(session.authenticated ? session.username || null : null)).catch(() => setUsername(null)); };
    loadSession();
    window.addEventListener("xm:auth-changed", loadSession);
    return () => window.removeEventListener("xm:auth-changed", loadSession);
  }, []);
  return (
    <header className="site-header">
      <button className="wordmark" onClick={() => navigate("/")}><span>明</span><b>{t("小明生活工具箱")}</b></button>
      <nav><button onClick={() => navigate("/")}>{t("全部工具")}</button><button onClick={() => navigate("/account")}>{username || t("登录 / 注册")}</button><LanguageSwitcher /></nav>
    </header>
  );
}

function ToolIcon({ tool }: { tool: Tool }) {
  const [failed, setFailed] = useState(false);
  return <span className="tool-icon" style={{ background: tool.color }}>{tool.iconImage && !failed ? <img src={tool.iconImage} alt="" onError={() => setFailed(true)} /> : tool.icon}</span>;
}

function HomePage() {
  const { locale, tool: localizeTool } = useI18n();
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState("全部");
  const [favorites, setFavorites] = useState<string[]>(readFavorites);
  const normalized = query.trim().toLowerCase();
  const localizedCatalog = useMemo(() => visibleCatalog.map(localizeTool), [locale]);
  const categories = useMemo(() => ["全部", ...new Set(localizedCatalog.map((tool) => tool.categoryName))], [localizedCatalog]);
  const filtered = useMemo(() => localizedCatalog.filter((tool) => {
    const categoryMatch = category === "全部" || tool.categoryName === category;
    const text = `${tool.title} ${tool.subtitle} ${tool.categoryName}`.toLowerCase();
    return categoryMatch && (!normalized || text.includes(normalized));
  }), [category, normalized, localizedCatalog]);
  useEffect(() => { setCategory("全部"); }, [locale]);
  const toggleFavorite = (id: string) => {
    const next = favorites.includes(id) ? favorites.filter((item) => item !== id) : [...favorites, id];
    setFavorites(next);
    localStorage.setItem(FAVORITES_KEY, JSON.stringify(next));
  };

  return (
    <>
      <Header />
      <main className="home-shell">
        <section className="hero">
          <div className="hero-copy">
            <h1><span className="hero-title-line">生活里的小问题，</span><br /><em>在这里解决。</em></h1>
          </div>
          <div className="hero-orbit" aria-hidden="true"><div className="orbit-ring"><span>算</span><span>查</span><span>图</span><span>文</span></div><div className="orbit-core">明<small>TOOLBOX</small></div></div>
        </section>

        <section className="catalog-section">
          <div className="catalog-heading"><div><h2>找到你需要的工具</h2></div><div className="search-box"><span>⌕</span><input type="search" placeholder="搜索工具、用途或分类" value={query} onChange={(event) => setQuery(event.target.value)} /></div></div>
          <div className="category-strip">{categories.map((name) => <button key={name} className={category === name ? "active" : ""} onClick={() => setCategory(name)}>{name}</button>)}</div>
          {filtered.length ? <div className="tool-grid">{filtered.map((tool) => (
            <article className="tool-card" key={tool.id}>
              <button className="card-main" onClick={() => navigate(toolUrl(tool))}>
                <div className="card-top"><ToolIcon tool={tool} /></div>
                <h3>{tool.title}</h3><p>{tool.subtitle}</p><small>{tool.categoryName}</small>
              </button>
              <button className={`favorite ${favorites.includes(tool.id) ? "selected" : ""}`} aria-label={favorites.includes(tool.id) ? "取消收藏" : "收藏"} onClick={() => toggleFavorite(tool.id)}>{favorites.includes(tool.id) ? "★" : "☆"}</button>
            </article>
          ))}</div> : <div className="empty"><b>没有找到匹配的工具</b><p>换个关键词或分类试试。</p></div>}
        </section>

        <MonetizationPlacement placement="home" />

      </main>
    </>
  );
}

function ToolPage({ id }: { id: string }) {
  const { tool: localizeTool } = useI18n();
  const sourceTool = visibleCatalog.find((item) => item.id === id);
  if (!sourceTool) return <NotFound />;
  const tool = localizeTool(sourceTool);
  return (
    <>
      <Header />
      <main className="tool-page">
        <button className="back-link" onClick={() => navigate("/")}>← 返回全部工具</button>
        <section className="tool-hero">
          <ToolIcon tool={tool} />
          <div><p className="eyebrow">{tool.categoryName}</p><h1>{tool.title}</h1><p>{tool.subtitle}</p></div>
        </section>
        <FeatureAccessBoundary pageId={tool.id}><BusinessTool id={tool.id} title={tool.title} /></FeatureAccessBoundary>
        <MonetizationPlacement placement="tool" />
      </main>
    </>
  );
}

function NotFound() {
  return <main className="not-found"><div className="standalone-language-switcher"><LanguageSwitcher /></div><p className="eyebrow">404</p><h1>这里没有这个工具</h1><button className="primary-button" onClick={() => navigate("/")}>返回工具箱</button></main>;
}

type PaymentOrderView = { id: string; product_name: string; amount_minor: number; currency: string; status: string };

function PaymentCheckoutPage({ productId }: { productId: string }) {
  const [error, setError] = useState("");
  const [order, setOrder] = useState<PaymentOrderView | null>(null);
  const [qrImage, setQrImage] = useState("");
  const [waiting, setWaiting] = useState(true);
  useEffect(() => {
    let stopped = false;
    let orderId = "";
    let timer = 0;
    const updateOrder = async () => {
      if (!orderId || stopped) return;
      try {
        const response = await fetch(`/api/payments/orders/${orderId}`);
        const data = await response.json() as { order?: PaymentOrderView; error?: string };
        if (!response.ok || !data.order) return;
        if (stopped) return;
        setOrder(data.order);
        if (data.order.status !== "pending") {
          window.clearInterval(timer);
          if (data.order.status === "paid") window.location.replace(`/payment/result?order=${encodeURIComponent(orderId)}`);
        }
      } catch { /* the next poll retries */ }
    };
    void fetch("/api/payments/checkout", { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ productId, visitorId: getBrowserVisitorId() }) })
      .then(async (response) => {
        const data = await response.json() as { orderId?: string; mode?: string; checkoutUrl?: string; qrCode?: string; error?: string };
        if (!response.ok) throw new Error(data.error || "无法创建付款订单");
        orderId = data.orderId || "";
        if (data.mode === "redirect" && data.checkoutUrl) window.location.assign(data.checkoutUrl);
        else if (data.qrCode) setQrImage(await QRCode.toDataURL(data.qrCode, { width: 260, margin: 1 }));
        setWaiting(false);
        await updateOrder();
        timer = window.setInterval(() => void updateOrder(), 3000);
      })
      .catch((loadError) => { if (!stopped) { setError(loadError instanceof Error ? loadError.message : "付款页面加载失败"); setWaiting(false); } });
    return () => { stopped = true; window.clearInterval(timer); };
  }, [productId]);

  return <main className="payment-flow-page"><div className="standalone-language-switcher"><LanguageSwitcher /></div><button className="back-link" onClick={() => navigate("/")}>← 返回工具箱</button><section className="payment-flow-card"><div className="brand-mark">明</div><h1>{order?.product_name || "收银台"}</h1>{order && <p className="payment-flow-amount">¥{(order.amount_minor / 100).toFixed(2)}</p>}{waiting && <p>正在创建收银台…</p>}{error && <p className="error-message">{error}</p>}{qrImage && <><img className="payment-qr-image" src={qrImage} alt="支付宝付款二维码" /><p>请使用支付宝扫码付款</p></>}{order?.status === "pending" && <small>等待支付结果…</small>}</section></main>;
}

function PaymentResultPage() {
  const orderId = new URLSearchParams(window.location.search).get("order") || "";
  const [order, setOrder] = useState<PaymentOrderView | null>(null);
  useEffect(() => {
    let stopped = false;
    const load = async () => {
      if (!orderId) return;
      try {
        const response = await fetch(`/api/payments/orders/${encodeURIComponent(orderId)}`);
        const data = await response.json() as { order?: PaymentOrderView };
        if (!stopped && data.order) setOrder(data.order);
      } catch { /* shown as pending until the next refresh */ }
    };
    void load();
    const timer = window.setInterval(() => void load(), 3000);
    return () => { stopped = true; window.clearInterval(timer); };
  }, [orderId]);
  const status = order?.status === "paid" ? "支付成功" : order?.status === "failed" ? "支付失败" : order?.status === "expired" ? "订单已过期" : order?.status === "refunded" ? "已退款" : "正在确认支付";
  return <main className="payment-flow-page"><div className="standalone-language-switcher"><LanguageSwitcher /></div><section className="payment-flow-card"><div className="brand-mark">明</div><h1>{status}</h1>{order && <><p>{order.product_name}</p><p className="payment-flow-amount">¥{(order.amount_minor / 100).toFixed(2)}</p></>}<button className="primary-button" onClick={() => navigate("/")}>返回工具箱</button></section></main>;
}

export function App() {
  const pathname = usePathname();
  const activeVisit = useRef<{ id: number; token: string; path: string } | null>(null);
  useEffect(() => {
    let cancelled = false;
    const previous = activeVisit.current;
    if (previous) {
      endPageVisit(previous.token);
      activeVisit.current = null;
    }
    void getDeviceDetails().then((deviceInfo) => fetch("/api/visit", { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ action: "start", path: pathname, browserId: getBrowserVisitorId(), deviceInfo }) }))
      .then((response) => response.json() as Promise<{ id?: number; token?: string }>)
      .then((result) => {
        if (!result.token || !result.id) return;
        if (cancelled) endPageVisit(result.token);
        else activeVisit.current = { id: result.id, token: result.token, path: pathname };
      }).catch(() => {});
    return () => {
      cancelled = true;
      if (activeVisit.current?.path === pathname) {
        endPageVisit(activeVisit.current.token);
        activeVisit.current = null;
      }
    };
  }, [pathname]);
  useEffect(() => {
    const endCurrentPage = () => {
      if (!activeVisit.current) return;
      endPageVisit(activeVisit.current.token);
      activeVisit.current = null;
    };
    window.addEventListener("pagehide", endCurrentPage);
    return () => window.removeEventListener("pagehide", endCurrentPage);
  }, []);
  useEffect(() => {
    const record = (nativeEvent: Event) => {
      const visit = activeVisit.current;
      if (!visit) return;
      const target = nativeEvent.target instanceof HTMLElement ? nativeEvent.target : null;
      if (!target) return;
      const isInputEvent = nativeEvent.type === "input";
      const isChangeEvent = nativeEvent.type === "change";
      const isSubmitEvent = nativeEvent.type === "submit";
      const control = target.closest<HTMLElement>("button, a, input, textarea, select, [role='button'], [contenteditable='true'], summary") || target;
      if (isInputEvent && !(target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement || target.isContentEditable)) return;
      if (isChangeEvent && !(target instanceof HTMLSelectElement || target instanceof HTMLInputElement && ["checkbox", "radio"].includes(target.type))) return;
      if (isSubmitEvent && !(target instanceof HTMLFormElement)) return;

      const input = target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement || target instanceof HTMLSelectElement ? target : null;
      const autocomplete = input?.getAttribute("autocomplete") || "";
      const fieldName = `${input?.getAttribute("name") || ""} ${input?.id || ""} ${autocomplete}`;
      const sensitive = Boolean(input && (input instanceof HTMLInputElement && ["password", "hidden", "file"].includes(input.type)
        || /password|passwd|secret|token|otp|verification|验证码|one-time-code|cc-/i.test(fieldName)
        || input.hasAttribute("data-private")));
      if (isInputEvent && sensitive) return;

      const labels = input instanceof HTMLInputElement || input instanceof HTMLTextAreaElement ? Array.from(input.labels || []).map((label) => label.innerText).join(" ") : "";
      const component = (control.getAttribute("data-component") || control.getAttribute("aria-label") || control.getAttribute("title") || labels || control.getAttribute("placeholder") || (control.innerText || "").trim() || control.tagName.toLowerCase()).replace(/\s+/g, " ").slice(0, 160);
      const componentType = input instanceof HTMLInputElement ? `input:${input.type}${sensitive ? ":secret" : ""}` : target instanceof HTMLTextAreaElement ? `textarea${sensitive ? ":secret" : ""}` : target instanceof HTMLSelectElement ? "select" : target.tagName.toLowerCase();
      let value = "";
      if (isInputEvent && !sensitive) value = target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement ? target.value : target.textContent || "";
      else if (isChangeEvent && target instanceof HTMLInputElement) value = `${target.checked ? "已勾选" : "未勾选"} · ${target.value}`;
      else if (isChangeEvent && target instanceof HTMLSelectElement) value = Array.from(target.selectedOptions).map((option) => option.label).join("、");
      const action = isInputEvent ? "输入" : isChangeEvent ? "选择" : isSubmitEvent ? "提交表单" : "点击";
      void fetch("/api/visit", {
        method: "POST", headers: { "content-type": "application/json" }, keepalive: true,
        body: JSON.stringify({ action: "event", token: visit.token, component, componentType, event: action, value }),
      }).catch(() => {});
    };
    document.addEventListener("click", record, true);
    document.addEventListener("input", record, true);
    document.addEventListener("change", record, true);
    document.addEventListener("submit", record, true);
    return () => {
      document.removeEventListener("click", record, true);
      document.removeEventListener("input", record, true);
      document.removeEventListener("change", record, true);
      document.removeEventListener("submit", record, true);
    };
  }, []);
  if (pathname.startsWith("/admin")) return <AdminPage />;
  return <I18nProvider><LocalizedContent><UserRoutes pathname={pathname} /></LocalizedContent></I18nProvider>;
}

function UserRoutes({ pathname }: { pathname: string }) {
  if (pathname === "/payment/result") return <PaymentResultPage />;
  if (pathname.startsWith("/checkout/")) return <PaymentCheckoutPage productId={decodeURIComponent(pathname.slice("/checkout/".length))} />;
  if (pathname === "/account") return <AccountPage />;
  if (pathname === "/") return <HomePage />;
  if (pathname.startsWith("/tool/")) return <ToolPage id={decodeURIComponent(pathname.slice(6))} />;
  return <NotFound />;
}

function endPageVisit(token: string): void {
  const body = new Blob([JSON.stringify({ action: "end", token })], { type: "application/json" });
  if (!navigator.sendBeacon("/api/visit", body)) {
    void fetch("/api/visit", { method: "POST", headers: { "content-type": "application/json" }, body, keepalive: true }).catch(() => {});
  }
}
